import Kuroshiro from 'kuroshiro';
import kuromoji from 'kuromoji';
import KuromojiAnalyzer from 'kuroshiro-analyzer-kuromoji'

class JapaneseTextService {
  // Call mode (LAN-861) only converts words with kuroshiro, which loads its own
  // copy of the dictionary; it passes eagerTokenizer: false to skip this one.
  constructor(pronunciationCharacters, { eagerTokenizer = true } = {}) {
    this.boldChars = ['*', '**', '<b>', '</b>'];
    this.delChars = ['<del>', '</del>'];
    this.specialChars = ['→', ' ', ',', ':', '*', '**', '<b>', '</b>', '"', '<del>', '</del>'];
    this.kuroshiro = new Kuroshiro();
    this.initialized = false
    this.initializationPromise = null
    this.pronunciationCharacters = pronunciationCharacters
    this.tokenizer = null;
    if (eagerTokenizer) this.initTokenizer();
    this.isBoldOpen = false;
    this.isDelOpen = false;
  }

  async initTokenizer() {
    return new Promise((resolve, reject) => {
      kuromoji.builder({ dicPath: '/packs/dict/' }).build((err, tokenizer) => {
        if (err) {
          console.error('Error initializing Kuromoji:', err);
          reject(err);
          return;
        }
        this.tokenizer = tokenizer;
        resolve();
      });
    });
  }

  async initialize() {
    if (this.initialized) {
      return
    }

    if (this.initializationPromise) {
      return this.initializationPromise
    }

    // Update path to match webpack's output directory
    const kuroshiroAnalyzer = new KuromojiAnalyzer({
      dictPath: '/packs/dict'  // Point to webpack's output directory
    });

    this.initializationPromise = this.kuroshiro.init(kuroshiroAnalyzer)
      .then(() => {
        this.initialized = true
        // Set Utils after initialization
        this.kuroshiroUtils = Kuroshiro.Util;  // Note: It's Kuroshiro.Util, not kuroshiro.Utils
      })
      .catch(error => {
        console.error('Failed to initialize Kuroshiro:', error)
        throw error
      })

    return this.initializationPromise
  }

  // Flagged feature to segment text into characters
  async segment(text) {
    if (!this.tokenizer) {
      await this.initTokenizer();
    }
    // console.log('Segmenting text:', text);
    const tokens = this.tokenizer.tokenize(text);
    // Extract only the surface_form (characters) from each token
    const segmentedCharacters = tokens.map(token => token.surface_form);
    // console.log('Segmented result:', segmentedCharacters);
    return segmentedCharacters;
  }

  async processText(text, shouldCreateReadingAids = true) {
    await this.initialize();

    // Only trim non-word space characters for Japanese text
    const utils = this.kuroshiroUtils || Kuroshiro.Util;
    const hasJapanese = utils.hasJapanese(text);
    
    let trimmedText;
    if (hasJapanese) {
      // For Japanese text, trim non-word space characters
      trimmedText = text.replace(/[\s\u200B\u200C\u200D\u2060\uFEFF]+/g, ' ').trim();
    } else {
      // For non-Japanese text, preserve spaces but trim leading/trailing
      trimmedText = text.replace(/[\s\u200B\u200C\u200D\u2060\uFEFF]+/g, ' ');
    }
    
    // Pre-process special characters
    let processedText = trimmedText;
    let segments = [];
    let isBoldOpen = false;
    let isDelOpen = false;

    // Split text by special characters while preserving them
    let currentIndex = 0;
    while (currentIndex < processedText.length) {
      let foundSpecial = false;
      
      // Check for each special character
      for (const specialChar of this.specialChars) {
        if (processedText.startsWith(specialChar, currentIndex)) {
          // If we have accumulated text before this special char, process it
          if (currentIndex > 0) {
            const textBefore = processedText.slice(0, currentIndex);
            const textSegments = await this.segment(textBefore);
            segments = segments.concat(textSegments.map(s => ({ 
              origin: s,
              isBoldOpen,
              isDelOpen
            })));
          }
          
          // Update state based on special character
          if (specialChar === '*' || specialChar === '**' || specialChar === '<b>') {
            isBoldOpen = !isBoldOpen;
          } else if (specialChar === '<del>') {
            isDelOpen = true;
          } else if (specialChar === '</del>') {
            isDelOpen = false;
          }
          
          // Add the special character as its own segment
          segments.push({
            origin: specialChar,
            isSpecial: true,
            isBoldOpen,
            isDelOpen
          });
          
          // Update the processed text and index
          processedText = processedText.slice(currentIndex + specialChar.length);
          currentIndex = 0;
          foundSpecial = true;
          break;
        }
      }
      
      if (!foundSpecial) {
        currentIndex++;
      }
    }
    
    // Process any remaining text
    if (processedText.length > 0) {
      const remainingSegments = await this.segment(processedText);
      segments = segments.concat(remainingSegments.map(s => ({ 
        origin: s,
        isBoldOpen,
        isDelOpen
      })));
    }

    if (!shouldCreateReadingAids || !hasJapanese) {
      return this.processSegmentWithoutReadingAids(segments);
    }

    // if pronunciationCharacters is romaji - check for any japanese;
    // if pronunciationCharacters is hiragana or katakana
    if (this.pronunciationCharacters === 'romaji' && !utils.hasJapanese(trimmedText)) {
      return this.processSegmentWithoutReadingAids(segments);
    }
    if (this.pronunciationCharacters === 'hiragana' && !utils.hasKanji(trimmedText) || this.pronunciationCharacters === 'katakana' && !utils.hasKanji(trimmedText)) {
      return this.processSegmentWithoutReadingAids(segments);
    }
    
    const processedSegments = await Promise.all(
      segments.map(this.processSegment.bind(this))
    );
    return processedSegments;
  }

  processSegmentWithoutReadingAids(segments) {
    let isBoldOpen = false;
    let currentText = '';
    let result = [];

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];

      // Handle special characters
      if (segment.isSpecial) {
        if (segment.origin === ' ' || segment.origin === '\u200B' || segment.origin === '\u200C' || segment.origin === '\u200D' || segment.origin === '\u2060' || segment.origin === '\uFEFF') {
          // Convert spaces to &nbsp;
          result.push({
            html: '&nbsp;',
            text: ' ',
            hasTranslation: false
          });
        } else if (segment.origin === '*' || segment.origin === '**' || segment.origin === '<b>') {
          isBoldOpen = !isBoldOpen;
          result.push({
            html: isBoldOpen ? '<b>' : '</b>',
            text: '',
            hasTranslation: false
          });
        } else if (segment.origin === '<del>') {
          result.push({
            html: '<del>',
            text: '',
            hasTranslation: false
          });
        } else if (segment.origin === '</del>') {
          result.push({
            html: '</del>',
            text: '',
            hasTranslation: false
          });
        } else {
          // For other special characters, keep them as is
          result.push({
            html: segment.origin,
            text: segment.origin,
            hasTranslation: false
          });
        }
      } else {
        // For regular text, check if it should be wrapped in bold tags
        const text = segment.origin;
        if (isBoldOpen) {
          result.push({
            html: `<b>${text}</b>`,
            text: text,
            hasTranslation: this.containsJapanese(text)
          });
        } else {
          result.push({
            html: text,
            text: text,
            hasTranslation: this.containsJapanese(text)
          });
        }
      }
    }

    return result;
  }

  async processSegment(segment) {
    // Handle special characters directly
    if (segment.isSpecial && !this.boldChars.includes(segment.origin) && !this.delChars.includes(segment.origin)) {
      return {
        html: segment.origin,
        text: segment.origin,
        hasTranslation: this.containsJapanese(segment.origin)
      };
    }

    if (this.boldChars.includes(segment.origin) || this.delChars.includes(segment.origin)) {
      return {
        html: '',
        text: '',
        hasTranslation: false
      };
    }

    try {
      // Convert to hiragana and add furigana readings
      const result = await this.kuroshiro.convert(segment.origin, {
        mode: 'furigana',
        to: this.pronunciationCharacters,
      });

      let html = result;
      if (segment.isDelOpen) {
        html = `<del>${html}</del>`;
      }
      if (segment.isBoldOpen) {
        html = `<b>${html}</b>`;
      }

      return {html, text: segment.origin, hasTranslation: this.containsJapanese(segment.origin) };
    } catch (error) {
      console.error('Error processing Japanese text:', error)
      return {html: segment.origin, text: segment.origin, hasTranslation: this.containsJapanese(segment.origin)}; // Return original segment if processing fails
    }
  }

  // Check if text contains Japanese characters
  containsJapanese(text) {
    // Use the static Util if instance utils not available
    const utils = this.kuroshiroUtils || Kuroshiro.Util;
    return utils.hasJapanese(text);
  }

  messagesContainsJapanese(messages) {
    if (!messages || messages.length === 0) return false;
    // convert messages to an array
    const messagesArray = Array.from(messages);
    // console.log(typeof messagesArray );
    return messagesArray.some(message => this.containsJapanese(message.textContent));
  }

  processSpanElements(spanElements) {
    // Convert NodeList to Array for easier processing
    const elements = Array.from(spanElements);
    const groups = [];
    let currentLine = [];
    let isCurrentLineJapanese = false;

    // Helper function to process and add a line to groups
    const addLineToGroups = () => {
      if (currentLine.length > 0) {
        // Check if any element in the line contains Japanese
        isCurrentLineJapanese = currentLine.some(element => 
          this.containsJapanese(element.textContent)
        );
        
        groups.push({
          translate: isCurrentLineJapanese,
          elements: currentLine
        });
        currentLine = [];
        isCurrentLineJapanese = false;
      }
    };

    // Iterate through all elements
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      
      // If we hit a BR tag, start a new line group
      if (element.tagName === 'BR') {
        addLineToGroups();
        continue;
      }
      
      // Add element to current line
      currentLine.push(element);
    }

    // Add any remaining line
    if (currentLine.length > 0) {
      addLineToGroups();
    }

    return groups;
  }
}

// Create and export a singleton instance
export default JapaneseTextService;
